import { useEffect, useRef, useState } from 'react'

import { t } from '../../../../common/i18n.js'
import { Language } from '../../../../common/types/i18n.js'
import { BalanceTimeActiveUser } from '../../../../common/types/playtime.js'
import {
    formatPeriodJumpLabel,
    formatPeriodTitle,
} from '../../../../common/utils/format-utils.js'
import { Button } from '../../../components/button/button.js'
import { ChevronDown, ChevronLeft, ChevronRight } from '../../../components/icon/solar-icons.js'
import { todayStr } from '../../portal/utils/format.js'

type RangeType = 'week' | 'month' | 'quarter' | 'year'

const RANGE_KEYS: RangeType[] = ['week', 'month', 'quarter', 'year']

// ─── Types ──────────────────────────────────────────────────────────────────────

export interface BalanceTimeFilterProps {
    rangeType: RangeType
    displayStartDate: string
    displayEndDate: string
    selectedUser: string
    users: BalanceTimeActiveUser[]
    canSelectUser: boolean
    lang: Language
    onRangeChange: (range: RangeType) => void
    onOffsetChange: (delta: number) => void
    onOffsetSet: (offset: number) => void
    onUserChange: (userId: string) => void
}

// ─── Custom User Selector ──────────────────────────────────────────────────────

interface UserSelectProps {
    value: string
    users: BalanceTimeActiveUser[]
    defaultLabel: string
    lang: Language
    onChange: (userId: string) => void
}

const UserSelect = ({ value, users, defaultLabel, lang, onChange }: UserSelectProps) => {
    const [isOpen, setIsOpen] = useState(false)
    const [search, setSearch] = useState('')
    const containerRef = useRef<HTMLDivElement>(null)

    const selectedUser = users.find((u) => String(u.id) === value)
    const displayText = selectedUser?.name ?? defaultLabel

    const filteredUsers = users.filter(
        (u) => !search || (u.name ?? '').toLowerCase().includes(search.toLowerCase()),
    )

    // Close on outside click
    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setIsOpen(false)
                setSearch('')
            }
        }
        if (isOpen) document.addEventListener('mousedown', handleClick)
        return () => document.removeEventListener('mousedown', handleClick)
    }, [isOpen])

    return (
        <div className="user-select" ref={containerRef}>
            <button
                type="button"
                className="user-select__trigger"
                onClick={() => setIsOpen(!isOpen)}
            >
                <span className="user-select__text">{displayText}</span>
                <span className={`user-select__chevron ${isOpen ? 'is-open' : ''}`}>
                    <ChevronDown size={16} />
                </span>
            </button>

            {isOpen && (
                <div className="user-select__dropdown">
                    <input
                        type="text"
                        className="user-select__search"
                        placeholder={t.common.search(lang)}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        autoFocus
                    />
                    <ul className="user-select__list">
                        <li
                            className={`user-select__option ${!value ? 'is-selected' : ''}`}
                            onClick={() => {
                                onChange('')
                                setIsOpen(false)
                                setSearch('')
                            }}
                        >
                            {defaultLabel}
                        </li>
                        {filteredUsers.map((u) => (
                            <li
                                key={u.id}
                                className={`user-select__option ${String(u.id) === value ? 'is-selected' : ''}`}
                                onClick={() => {
                                    onChange(String(u.id))
                                    setIsOpen(false)
                                    setSearch('')
                                }}
                            >
                                {u.name}
                            </li>
                        ))}
                        {filteredUsers.length === 0 && (
                            <li className="user-select__empty">{t.common.noResults(lang)}</li>
                        )}
                    </ul>
                </div>
            )}
        </div>
    )
}

// ─── Filter Component ───────────────────────────────────────────────────────────

/**
 * Harvest-style period bar: a bold period title, prev / "jump to current" /
 * next navigation, and a unit dropdown. Every change reloads immediately (the
 * consumers auto-load on range/offset change), so there is no "load" button.
 */
export const BalanceTimeFilter = (props: BalanceTimeFilterProps) => {
    const bt = t.playtime.balancetime
    const { lang, rangeType } = props

    const currentLabel = {
        week: bt.thisWeek,
        month: bt.thisMonth,
        quarter: bt.thisQuarter,
        year: bt.thisYear,
    }[rangeType]

    // "See kuu" is only true while the shown period contains today; once the
    // user pages away the button names the shown period instead (TASK-303).
    const jumpLabel = formatPeriodJumpLabel(
        rangeType,
        props.displayStartDate,
        props.displayEndDate,
        todayStr(),
        lang,
    )

    return (
        <div className="balancetime-filter">
            <h2 className="balancetime-filter__title">
                {formatPeriodTitle(
                    rangeType,
                    props.displayStartDate,
                    props.displayEndDate,
                    lang,
                )}
            </h2>

            <div className="balancetime-filter__controls">
                <div className="balancetime-filter__nav">
                    <Button
                        text={bt.previous(lang)}
                        iconOnly
                        iconLeft={ChevronLeft}
                        size="small"
                        variant="secondary"
                        onClick={() => props.onOffsetChange(-1)}
                    />
                    <button
                        type="button"
                        className="balancetime-filter__current-btn"
                        onClick={() => props.onOffsetSet(0)}
                    >
                        {jumpLabel ?? currentLabel(lang)}
                    </button>
                    <Button
                        text={bt.next(lang)}
                        iconOnly
                        iconLeft={ChevronRight}
                        size="small"
                        variant="secondary"
                        onClick={() => props.onOffsetChange(1)}
                    />
                </div>

                <div className="balancetime-filter__unit">
                    <select
                        className="balancetime-filter__unit-select"
                        value={rangeType}
                        onChange={(e) => props.onRangeChange(e.target.value as RangeType)}
                    >
                        {RANGE_KEYS.map((rt) => (
                            <option key={rt} value={rt}>
                                {bt[rt](lang)}
                            </option>
                        ))}
                    </select>
                    <ChevronDown size={16} className="balancetime-filter__unit-chevron" />
                </div>

                {props.canSelectUser && (
                    <UserSelect
                        value={props.selectedUser}
                        users={props.users}
                        defaultLabel={bt.myHours(lang)}
                        lang={lang}
                        onChange={props.onUserChange}
                    />
                )}
            </div>
        </div>
    )
}
